import { BrowserRouter } from 'react-router-dom';

import { Analytics } from '@vercel/analytics/react';
import { UserProvider } from './providers/UserProvider';
import { isPlatform } from './utilities/platform';
import * as Sentry from '@sentry/react';
import { config } from '@fortawesome/fontawesome-svg-core';
import '@fortawesome/fontawesome-svg-core/styles.css';

/* Theme variables */
import './theme/variables.index.css';
import { Router } from './routes';
import { ThemeConfig } from './theme';
import { WindowProvider } from './providers/WindowProvider';
import { MenuProvider } from './providers/MenuProvider';
import { PRODUCTION } from './config';
import { useEffect } from 'react';
import { SafeArea, StatusbarStyle } from 'capacitor-plugin-safe-area';
import { Capacitor } from '@capacitor/core';
import { SafeAreaDebug } from './components/SafeAreaDebug';
import { enableFullscreen } from './utilities/fullscreen';

config.autoAddCss = false;

export default function App(): JSX.Element {

    useEffect(() => {
        const initializeSafeArea = async () => {
            // Ne pas exécuter sur le web
            if (!isPlatform('hybrid')) {
                return;
            }

            try {
                // Activer le mode plein écran (masquer la barre de statut)
                await enableFullscreen();
                
                // Obtenir les insets initiaux
                const { insets } = await SafeArea.getSafeAreaInsets();

                // Appliquer les variables CSS
                for (const [key, value] of Object.entries(insets)) {
                    document.documentElement.style.setProperty(
                        `--safe-area-inset-${key}`,
                        `${value}px`,
                    );
                }

                console.log('Safe area insets initialized:', insets);

                // Sur Android, activer le mode immersif pour utiliser tout l'écran
                if (Capacitor.getPlatform() === 'android') {
                    await SafeArea.setImmersiveNavigationBar({
                        // Dark = texte/icônes noirs (pour fond clair)
                        // Light = texte/icônes blancs (pour fond sombre)
                        statusBarStyle: StatusbarStyle.Dark
                    });
                    console.log('Immersive navigation bar enabled on Android with Dark style');
                }

                // Écouter les changements de safe area (rotation, clavier, etc.)
                await SafeArea.addListener('safeAreaChanged', (data) => {
                    const { insets: newInsets } = data;
                    for (const [key, value] of Object.entries(newInsets)) {
                        document.documentElement.style.setProperty(
                            `--safe-area-inset-${key}`,
                            `${value}px`,
                        );
                    }
                    console.log('Safe area changed:', newInsets);
                });
            } catch (error) {
                console.error('Error initializing SafeArea:', error);
            }
        };

        initializeSafeArea();

        // Cleanup lors du démontage
        return () => {
            SafeArea.removeAllListeners();
        };
    }, []);

    return (
        <Sentry.ErrorBoundary
            fallback={({ error }) => <div>{error.toString()}</div>}>
            <div
                className={isPlatform('hybrid') ? 'safe-area-container' : ''}
                style={{
                    height: '100dvh',
                    width: '100%',
                }}
            >
                {/* Composant de debug - À RETIRER EN PRODUCTION */}
                {/* <SafeAreaDebug /> */}

                <ThemeConfig>
                    <BrowserRouter>
                        <UserProvider>
                            <WindowProvider>
                                <MenuProvider>
                                    <Router />
                                </MenuProvider>
                            </WindowProvider>
                        </UserProvider>
                    </BrowserRouter>
                </ThemeConfig>
            </div>
            {PRODUCTION && <Analytics />}
        </Sentry.ErrorBoundary>
    );
}
